<script setup lang="ts">
import type {
  IUserSelection,
  IViewData,
} from "@/stories/form/DatePicker/shared";
import ViewHeader from "@/stories/form/DatePicker/ViewHeader.vue";
import { computed } from "vue";
import { toUnix } from "@/stories/utils/dates";

const CURRENT_YEAR = new Date().getFullYear();

const CURRENT_MONTH = new Date().getMonth();

interface Props {
  view: IViewData;
  selection: IUserSelection | null;
  minDate?: number;
  maxDate?: number;
}

const props = defineProps<Props>();

defineEmits(["setView"]);

const months = computed(() => {
  const formatter = new Intl.DateTimeFormat(navigator.language || "da", {
    month: "short",
  });

  const months = Array.from({ length: 12 }, (_, i) =>
    formatter.format(new Date(2023, i, 1)),
  );

  return months;
});

const isSelectedMonth = (index: any) => {
  return (
    props.view.year === props.selection?.year &&
    props.selection?.month === index
  );
};

const isCurrentMonth = (index: any) => {
  return props.view.year === CURRENT_YEAR && index === CURRENT_MONTH;
};

const isMonthOutOfRange = (monthIndex: number): boolean => {
  const { minDate, maxDate } = props;
  if (!minDate && !maxDate) return false;

  // Check if entire month is out of range
  const firstDayOfMonth = toUnix(
    new Date(props.view.year, monthIndex, 1, 0, 0, 0),
  );
  const lastDayOfMonth = toUnix(
    new Date(props.view.year, monthIndex + 1, 0, 23, 59, 59),
  );

  // Month is out of range if it's completely before minDate or completely after maxDate
  if (minDate && lastDayOfMonth < minDate) return true;
  if (maxDate && firstDayOfMonth > maxDate) return true;

  return false;
};
</script>

<template>
  <div>
    <ViewHeader
      @prev-click="$emit('setView', { year: props.view.year - 1 })"
      @next-click="$emit('setView', { year: props.view.year + 1 })"
      @title-click="$emit('setView', { type: 'years' })"
    >
      {{ props.view.year }}
    </ViewHeader>
    <div class="grid grid-cols-3 gap-2">
      <button
        type="button"
        v-for="(month, index) of months"
        :key="index"
        :class="[
          'w-full py-3 text-center rounded-lg text-neutral1000 text-lg font-semibold focusVisible test-month-selector focus-visible-strong',
          isSelectedMonth(index)
            ? 'bg-darkOcean100 border border-darkOcean500 focus-visible:border-transparent'
            : '',
          isCurrentMonth(index) && !isSelectedMonth(index)
            ? 'bg-neutral50 text-darkOcean500 font-bold'
            : '',
          { 'opacity-40': isMonthOutOfRange(index) },
        ]"
        @click="$emit('setView', { month: index, type: 'days' })"
      >
        {{ month }}
      </button>
    </div>
  </div>
</template>
